"use client";
import React, { Context, createContext, useContext, useMemo } from "react";
import { I18nHive, mixInLToLeafs } from "../localization";
import { Localization } from "../localization-auto-generated";
import { structuredClone } from "../structured-clone";
const getContext = (() => {
    // This is self executing to keep the `privateContextMap` inaccessible to anything else.
    // It forces the only access to it to be via the strongly typed method and thereby enforcing the correct type output of every call.
    const privateContextMap = new Map<
        keyof Localization,
        Context<I18nHive<any>>
    >();
    return <L extends keyof Localization>(l: L): Context<I18nHive<L>> => {
        return privateContextMap.has(l)
            ? privateContextMap.get(l)!
            : privateContextMap
                  .set(l, createContext<I18nHive<L>>(null!))
                  .get(l)!;
    };
})();

type I18nProps<T extends keyof Localization> = React.PropsWithChildren<{
    localizationContext: I18nHive<T>;
}>;

export const I18NContextProviderClient = <T extends keyof Localization>(
    props: I18nProps<T>,
) => {
    const { children, localizationContext } = props;

    const cachedContext = useMemo(() => {
        const memoedContext = structuredClone(localizationContext);
        mixInLToLeafs(memoedContext);
        return memoedContext;
    }, [localizationContext]);
    // This Cast is safe because `localizationContext` is a `Pick<Localization,T>` under the hood,
    // and so it must have exactly 1 `key` which must be of type `T`
    const t = Object.keys(localizationContext)[0] as T;
    const ctx = getContext(t);
    return <ctx.Provider value={cachedContext}>{children}</ctx.Provider>;
};

export const useI18n = <L extends keyof Localization>(l: L): I18nHive<L> => {
    const context = useContext(getContext(l));
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (context === null) {
        throw new Error(
            `Localization Context for \`${l}\` not found. Ensure that the I18NContextProviderClient is wrapping the component tree.`,
        );
    }
    return context;
};
